import { ChangeDetectionStrategy, Component, OnInit, signal } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { delay, filter, map, tap } from 'rxjs';
import {
	fetchAllEvents,
	getAllEventsData,
	getUniversityEventsStateIsLoading,
} from '@uc/web/shared/data-access';
import { ActivatedRoute } from '@angular/router';
import { getStateUniversity } from '../../data-access/+state/university.selectors';
import {
	ClientFeatures,
	FeaturedPostgraduate,
	FeaturedUndergraduate,
	Gallery,
	UniversityEvent,
	UniversityEventResponse,
	UniversityProfile,
} from '@uc/web/shared/data-models';
import { AsyncPipe, formatDate } from '@angular/common';
import {
	UNI_SLIDER_COPY,
	UniversitySliderComponent,
} from '@uc/web/shared/feature/feature-university-slider';
import { fetchUniversity } from '../../data-access/+state/university.actions';
import {
	UniversityGTMService,
	UniversityReadUrlService,
	ClientValidatorService,
	getExternalLinks,
	NA,
} from '@uc/web/core';
import { SeoMetadataService, isStoreEmpty } from '@uc/utilities';
import { UniversitySkeletonComponent } from '../../components/university-skeleton/university-skeleton.component';
import { UniversityHeaderTitleComponent } from '../../components/university-header-title/university-header-title.component';
import { CtaOpenDaysBtnsComponent } from '../../components/cta-open-days-btns/cta-open-days-btns.component';
import { HeadingComponent } from '@uc/shared/ui';
import { OpenDaysRecommendedEventsWrapperComponent } from '../../components/open-days-recommended-events-wrapper/open-days-recommended-events-wrapper.component';
import {
	EventCardComponent,
	NoOpenDaysComponent,
	OurLocationComponent,
} from '@web/shared/ui';
import { RecommendedUnisGridComponent } from '@uc/web/shared/feature/feature-recommended-universities';
import { Product, ProductEnum, UppercaseProductEnum } from '@uc/shared/data-models';

@Component({
	selector: 'uc-open-days',
	templateUrl: './open-days.component.html',
	imports: [
		AsyncPipe,
		UniversitySkeletonComponent,
		UniversityHeaderTitleComponent,
		CtaOpenDaysBtnsComponent,
		HeadingComponent,
		OpenDaysRecommendedEventsWrapperComponent,
		EventCardComponent,
		NoOpenDaysComponent,
		OurLocationComponent,
		RecommendedUnisGridComponent,
		UniversitySliderComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OpenDaysComponent implements OnInit {
	readonly paidFeatures = [ClientFeatures.Undergraduate, ClientFeatures.Postgraduate];
	uniSlug = this._route.parent?.snapshot.params['uniName'];
	uniGallery!: Gallery;
	product: Product = ProductEnum.Undergraduate;
	/**
	 * when the user navigates to a different profile (e.g. undergraduate -> postgraduate)
	 * @isLoading$ will be true and the UI will display the loading state
	 */
	isLoading$ = this._store.pipe(select(getUniversityEventsStateIsLoading));
	isFeatured!: FeaturedUndergraduate | FeaturedPostgraduate;
	componentName = 'open-days';
	uniName = '';
	uniSliderCopy = signal({ ...UNI_SLIDER_COPY.uni.openDaysNonClient(this.uniName) });
	universityAddress!: string[];

	recommendedEvents: UniversityEvent[] = [];
	undergraduateEvents: UniversityEvent[] = [];
	postgraduateEvents: UniversityEvent[] = [];
	featuredEvent = signal<UniversityEvent | null>(null);

	externalUrl = signal('');
	externalEventsUrl = signal('');

	events$ = this._store.pipe(
		select(getAllEventsData),
		tap((data) => {
			const events = data.universityEvents;
			if (events && events.length) {
				this._allocateEvents(data);
				this._setHeaderData(events[0]);
			} else {
				this._setHeaderData();
			}
			return data;
		}),
	);

	university$ = this._store.select(getStateUniversity).pipe(
		delay(1),
		filter((university) => !isStoreEmpty(university)),
		map((university) => {
			if (!university) return;
			this.uniName = university.name;
			this.isFeatured = this._cvSrv.checkFeature(
				university?.paid_features,
				this.paidFeatures,
			);

			// Set product variable to PG depending on paid Features.
			this._setProductType(university);
			this._getExternalLinks(university);

			this.uniGallery = {
				cover_image: university.undergraduate.cover_image,
				images: university.universityGallery.images,
			};

			this.universityAddress = university.address.street.split(',');

			this._setSeo(university);

			this.uniSliderCopy.update((state) => {
				return {
					...state,
					...UNI_SLIDER_COPY.uni.openDaysNonClient(this.uniName),
				};
			});

			// GTM event
			const { id, name, order, address } = university;
			const region = address?.region || NA;
			this._onOpenDaysImpression(id, name, order, region);

			this._readUrlSrv.setProductManually(this.product);
			return university;
		}),
	);

	constructor(
		private _store: Store,
		private _route: ActivatedRoute,
		private _cvSrv: ClientValidatorService,
		private _uniGTMSrv: UniversityGTMService,
		private _seoSrv: SeoMetadataService,
		private _readUrlSrv: UniversityReadUrlService,
	) {}

	ngOnInit(): void {
		this._store.dispatch(fetchAllEvents({ uniSlug: this.uniSlug }));
		this._store.dispatch(
			fetchUniversity({ universityUrl: this.uniSlug, product: 'undergraduate' }),
		);

		if (this._route.parent?.snapshot.queryParams['preview'] === 'true') {
			this.paidFeatures.push(
				ClientFeatures.UnderPreview,
				ClientFeatures.PostPreview,
			);
		}
	}

	private _setProductType(university: UniversityProfile) {
		const isFeaturedPostgrad = this._cvSrv.checkFeature(
			university?.paid_features,
			ClientFeatures.Postgraduate,
		);
		const isFeaturedUndergrad = this._cvSrv.checkFeature(
			university?.paid_features,
			ClientFeatures.Undergraduate,
		);

		if (isFeaturedPostgrad && !isFeaturedUndergrad) {
			this.product = ProductEnum.Postgraduate;
		}
	}

	private _setHeaderData(event?: UniversityEvent) {
		if (event) {
			this.uniSliderCopy.update((state) => {
				return {
					...state,
					text: event.description,
					subtitle: event.subtitle,
					heading: formatDate(event.date, 'dd MMMM yyyy', 'en-GB'),
				};
			});
		} else if (this.isFeatured) {
			this.uniSliderCopy.update((state) => {
				return { ...state, ...UNI_SLIDER_COPY.uni.noOpenDays(this.uniName) };
			});
		}
	}

	private _allocateEvents(data: UniversityEventResponse): void {
		data.universityEvents.forEach((event) => {
			if (event.product === UppercaseProductEnum.Undergraduate) {
				this.undergraduateEvents.push(event);
			} else if (event.product === UppercaseProductEnum.Postgraduate) {
				this.postgraduateEvents.push(event);
			}

			if (event.is_recommended) {
				this.recommendedEvents.push(event);
			}
		});

		if (this.recommendedEvents.length > 0) {
			this.featuredEvent.set(this.recommendedEvents[0]);
		} else if (data.universityEvents.length > 0) {
			this.featuredEvent.set(data.universityEvents[0]);
		}
	}

	private _onOpenDaysImpression(
		uniId: number,
		uniName: string,
		ugSearchOrderPosition: number | null,
		region: string,
	) {
		this._uniGTMSrv.uniImpression(
			uniId,
			uniName,
			ugSearchOrderPosition,
			null,
			this.product,
			null,
			NA,
			NA,
			region,
			'Open-days',
		);
		this._uniGTMSrv.uniProfileView(
			uniId,
			uniName,
			ugSearchOrderPosition,
			null,
			this.product,
		);
	}

	private _setSeo(university: UniversityProfile) {
		this._seoSrv.updateTitle(`${university.name} Open Days`);
		this._seoSrv.updateDescription(
			`Discover the excitement of ${university.name} through an immersive Open Day or Virtual Event. Experience all this university has to offer and plan your future with them.`,
		);
	}

	private _getExternalLinks(university: UniversityProfile) {
		const { external_url, external_events_url } = getExternalLinks(
			university,
			['external_url', 'external_events_url'],
			this.product,
		);

		this.externalUrl.set(external_url);
		this.externalEventsUrl.set(external_events_url);
	}
}
